import {
  OrganizationService,
  PaidFlowTypesEnum,
  PaymentMethodTypesEnum,
  TransactionFlowTypesEnum,
} from "@propertelligent/client-api";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useEffect, useMemo } from "react";
import RightSideModal from "../Modal/right-side-modal";
import InputField from "../form-fields/InputField";
import SelectField from "../form-fields/SelectField";
import Card from "../card";
import apiHelper from "../../utils/apiHelper";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import DateHelper from "../../utils/DateHelper";
import { getEnumOptions } from "../../utils/enumHelpers";
import { useFieldChangesSnapshot } from "../../hooks/useFieldChangesSnapshot";
import type { TransactionRow } from "./transactionTypes";

interface DropdownOption {
  id?: number | null;
  accountName?: string | null;
  paymentMethodTypeId?: PaymentMethodTypesEnum | null;
}

interface EditTransactionDrawerProps {
  isOpen: boolean;
  transaction: TransactionRow | null;
  paymentMethods: DropdownOption[];
  payeeAccounts: DropdownOption[];
  onClose: () => void;
  onSaved: () => void;
}

interface EditTransactionFormValues {
  description: string;
  paymentMethodId: string;
  payeeAccountId: string;
  tranactionCreditDebitTypeId: string;
  amount: string;
  paymentDate: string;
  reference: string;
  transactionId: string;
}

const formId = "edit-transaction-form";
const TRANSACTION_AMOUNT_MAX = 99999999.99;
const TRANSACTION_AMOUNT_MAX_MESSAGE =
  "Amount cannot exceed 99,999,999.99.";

// Mirrors PaymentSummery.tsx / ContractorPaymentList.tsx / CurrentTenantPaymentList.tsx /
// ServiceRequestDetails.tsx's own local alias for the same enum.
const PAYMENT_MODE = PaymentMethodTypesEnum;

const toDateInputValue = (paymentDate?: string) =>
  paymentDate ? DateHelper.convertUTCtoDateTime2(paymentDate) : "";

// Sentinel for a transaction with no known payment-method type at all -- distinct from every
// real PaymentMethodTypesEnum member (all >= 0), so it can never collide with one.
const UNKNOWN_TYPE_SENTINEL = -1;

const toDefaultValues = (
  transaction: TransactionRow | null,
): EditTransactionFormValues => ({
  description: transaction?.description ?? "",
  // The select's value is always the payment method TYPE, not a row id (see paymentMethodOptions
  // below) -- a real row id can coincidentally equal another option's enum-value placeholder
  // (e.g. a row id of 7 colliding with Check's placeholder 7), which broke the value-based
  // lookup used to resolve the selected type. Keying everything by type instead makes that
  // collision structurally impossible: PaymentMethodTypesEnum members are inherently unique.
  paymentMethodId:
    transaction?.paymentMethodTypeId != null
      ? String(transaction.paymentMethodTypeId)
      : transaction?.paymentMethodId
        ? String(UNKNOWN_TYPE_SENTINEL)
        : "",
  payeeAccountId: transaction?.payeeAccountId
    ? String(transaction.payeeAccountId)
    : "",
  tranactionCreditDebitTypeId: transaction?.tranactionCreditDebitTypeId != null
    ? String(transaction.tranactionCreditDebitTypeId)
    : "",
  amount:
    transaction?.amount != null && !Number.isNaN(Number(transaction.amount))
      ? String(transaction.amount)
      : "",
  paymentDate: toDateInputValue(transaction?.paymentDate),
  reference: transaction?.reference ?? "",
  transactionId: transaction?.transactionId ?? "",
});

const EditTransactionDrawer = ({
  isOpen,
  transaction,
  paymentMethods,
  payeeAccounts,
  onClose,
  onSaved,
}: EditTransactionDrawerProps) => {
  const methods = useForm<EditTransactionFormValues>({
    mode: "onChange",
    defaultValues: toDefaultValues(transaction),
  });

  // RHF isDirty stays true after select revert when default was string and live value
  // is number ("11" !== 11). Snapshot String()-compares so Save re-disables correctly.
  const { hasFieldChanges, setSnapshot } = useFieldChangesSnapshot<EditTransactionFormValues>(
    methods.control,
    [
      "description",
      "paymentMethodId",
      "payeeAccountId",
      "tranactionCreditDebitTypeId",
      "amount",
      "paymentDate",
      "reference",
      "transactionId",
    ],
  );

  useEffect(() => {
    const defaults = toDefaultValues(transaction);
    methods.reset(defaults);
    setSnapshot(defaults);
  }, [methods, transaction, setSnapshot]);

  const {
    handleSubmit,
    formState: { isDirty, isSubmitting, isValid },
  } = methods;

  const hasUnsavedChanges = isDirty && hasFieldChanges;

  // Payment Method lists METHODS (Cash, Check, Assisted, Other), one option per manual type --
  // never account names (those are the "From" data) and never derived from the org's Active-filtered
  // PaymentMethod rows for the manual-type LIST itself (that list silently drops globally-seeded rows
  // like Cash/Check whenever their row happens to be IsActive:false, producing an inconsistent option
  // set per transaction). Instead this mirrors the exact pattern already used by PaymentSummery.tsx /
  // ContractorPaymentList.tsx / CurrentTenantPaymentList.tsx / ServiceRequestDetails.tsx: the fixed
  // getEnumOptions(PAYMENT_MODE, [...]) list decides which manual types are offered (filtered by
  // Payable vs Receivable), while the already-fetched `paymentMethods` prop (org's connected accounts,
  // same /organizationlist call those screens also make) is still used -- exactly like their own
  // savedMethodOptions -- to resolve the real account name for the one Plaid entry. The backend
  // resolves the real, internal seeded PaymentMethodId for the selected type server-side, so the
  // option's own Id value here is only a placeholder for manual types.
  // A Plaid-settled transaction's method is locked and injected as its own single option carrying the
  // transaction's real id: Plaid is bound to a specific connected account (the "From"), Delete/Refund
  // eligibility keys off the method's type (Plaid rows are not deletable; Refund is for Plaid), and
  // there is no account picker here, so switching to/from Plaid would silently rebind the account or
  // bypass those rules -- Plaid must never be selectable as a target for any transaction.
  const isPlaidTransaction =
    transaction?.paymentMethodTypeId === PAYMENT_MODE.Plaid;

  const paymentMethodOptions = useMemo(() => {
    const isReceivable =
      transaction?.paymentDueAccountPaidFlowTypeId === PaidFlowTypesEnum.Receiveable;
    const manualTypes = [
      PAYMENT_MODE.Cash,
      PAYMENT_MODE.Check,
      ...(isReceivable ? [PAYMENT_MODE.Assisted] : []),
      PAYMENT_MODE.Other,
    ];

    const currentId = transaction?.paymentMethodId;
    const currentTypeId = transaction?.paymentMethodTypeId;

    // Every option's Id is the type's own enum value -- never a real PaymentMethod row id.
    // PaymentMethodTypesEnum members are inherently unique, so no two options can ever share
    // a value (unlike reusing a real row id, which can coincidentally equal another option's
    // placeholder and make the value-based lookup below resolve to the wrong one).
    const options: { Id: number; Name: string; TypeId?: PaymentMethodTypesEnum }[] =
      getEnumOptions(PAYMENT_MODE, manualTypes).map((option) => ({
        ...option,
        TypeId: option.Id as PaymentMethodTypesEnum,
      }));

    // Plaid is never a selectable target -- shown only as the current, locked option, its
    // account name resolved the same way PaymentSummery.tsx's savedMethodOptions does. The real
    // connected-account id is sent from onSubmit directly off `transaction`, not this option's Id.
    if (isPlaidTransaction && currentId) {
      const connectedAccount = paymentMethods.find(
        (m) => m.paymentMethodTypeId === PAYMENT_MODE.Plaid && Number(m.id) === currentId,
      );
      options.unshift({
        Id: PAYMENT_MODE.Plaid,
        Name:
          connectedAccount?.accountName ??
          transaction?.formatted?.PaymentMethodTypeId ??
          PAYMENT_MODE[PAYMENT_MODE.Plaid],
        TypeId: PAYMENT_MODE.Plaid,
      });
    } else if (currentId && currentTypeId != null && !manualTypes.includes(currentTypeId)) {
      // The transaction's current type (e.g. Waived, Refund, Discount, or a legacy/deprecated
      // type like ACH/CreditCard/DebitCard/PayPal/Default) is not one of the fixed manual
      // options for this Payable/Receivable flow -- still surface it in place rather than
      // leaving the select with no matching option. This field stays editable (only Plaid locks
      // it) but must not silently show blank for these reachable rows (Edit is offered
      // unconditionally except for reversal rows -- see transactionActionUtils.ts).
      options.unshift({
        Id: currentTypeId,
        Name:
          transaction?.formatted?.PaymentMethodTypeId ??
          PAYMENT_MODE[currentTypeId],
        TypeId: currentTypeId,
      });
    } else if (currentId && currentTypeId == null) {
      // A row with no known type at all: keep showing the current value rather than a blank
      // select, keyed by the sentinel (never a real type value) so it can't collide either.
      options.unshift({
        Id: UNKNOWN_TYPE_SENTINEL,
        Name: transaction?.formatted?.PaymentMethodTypeId ?? "—",
        TypeId: undefined,
      });
    }

    return options;
  }, [
    transaction?.paymentDueAccountPaidFlowTypeId,
    transaction?.paymentMethodId,
    transaction?.paymentMethodTypeId,
    transaction?.formatted,
    isPlaidTransaction,
    paymentMethods,
  ]);

  // The saved method type must follow the selected option, not the transaction's original
  // one: the backend validates paymentMethodTypeId from the payload (e.g. Other requires a
  // Transaction ID), so a stale type would skip or wrongly enforce that rule after a change.
  const selectedPaymentMethodId = useWatch({
    control: methods.control,
    name: "paymentMethodId",
  });
  const selectedPaymentMethodTypeId =
    paymentMethodOptions.find(
      (option) => String(option.Id) === String(selectedPaymentMethodId),
    )?.TypeId ?? transaction?.paymentMethodTypeId;

  const payeeAccountOptions = useMemo(
    () =>
      payeeAccounts
        .filter((item) => Number(item?.id) > 0 && item?.accountName)
        .map((item) => ({
          Id: Number(item.id),
          Name: item.accountName as string,
        })),
    [payeeAccounts],
  );

  const transactionFlowOptions = useMemo(
    () =>
      getEnumOptions(
        TransactionFlowTypesEnum,
        Object.values(TransactionFlowTypesEnum).filter(
          (value): value is number => typeof value === "number",
        ),
      ),
    [],
  );

  const onSubmit = async (values: EditTransactionFormValues) => {
    if (!transaction?.id) {
      return;
    }

    // paymentMethodId is keyed by TYPE in the form field (see paymentMethodOptions), never a
    // real row id -- except when the selected type is unchanged from the transaction's own
    // current type, in which case the transaction's real id is sent instead. This isn't just
    // for Plaid (locked, must keep its own connected account): the backend only resolves a real
    // row by type for the seeded internal types (Cash/Check/Assisted/Other/Waived/Discount/
    // Refund) -- a transaction currently sitting on an unseeded legacy type (Default/CreditCard/
    // DebitCard/PayPal/ACH) has no internal row to resolve to, so sending that type's raw
    // numeric placeholder for an UNCHANGED selection would silently repoint the transaction at
    // whatever unrelated row happens to have that id. Only a genuine switch to one of the four
    // seeded manual types in the dropdown should ever send the type placeholder.
    const paymentMethodId =
      selectedPaymentMethodTypeId === transaction.paymentMethodTypeId
        ? transaction.paymentMethodId ?? Number(values.paymentMethodId)
        : Number(values.paymentMethodId);

    const payload = {
      id: transaction.id,
      description: values.description.trim(),
      paymentMethodId,
      payeeAccountId: Number(values.payeeAccountId),
      tranactionCreditDebitTypeId: Number(values.tranactionCreditDebitTypeId),
      amount: Number(values.amount),
      paymentDate: new Date(values.paymentDate).toISOString(),
      reference: values.reference.trim(),
      transactionId: values.transactionId.trim(),
      paymentDueAccountId: transaction.paymentDueAccountId,
      paymentMethodTypeId: selectedPaymentMethodTypeId,
      assistanceProvider:
        selectedPaymentMethodTypeId === PAYMENT_MODE.Assisted
          ? transaction.assistanceProvider ?? null
          : null,
      paymentMethodAccountName: transaction.paymentMethodAccountName ?? null,
      payeeAccountName: transaction.payeeAccountName ?? null,
    };

    try {
      const response = await apiHelper(
        OrganizationService.putApiOrganizationTransaction,
        {
          successMessage: "Successfully Updated Transaction",
          showNotification: true,
        },
        String(transaction.id),
        payload,
      );

      if (response?.data?.success) {
        onClose();
        onSaved();
        return;
      }

      if (!response?.statusCode && !response?.errors) {
        PubSub.publish(TopicsEnum.Notification.toString(), {
          type: NotificationType.Failure,
          message: "Failed to update transaction. Try again.",
        });
      }
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        type: NotificationType.Failure,
        message: "Failed to update transaction. Try again.",
      });
    }
  };

  return (
    <RightSideModal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Transaction"
      buttonlabel={isSubmitting ? "Saving..." : "Save"}
      formId={formId}
      maxWidth={550}
      isDirty={hasUnsavedChanges}
      isLoading={isSubmitting}
      isSubmitDisabled={isSubmitting || !hasUnsavedChanges || !isValid}
    >
      <Card className="no-bg">
        <FormProvider {...methods}>
          <form id={formId} onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
              <div className="col-md-12 mb-1">
                <InputField
                  name="description"
                  label="Description"
                  placeholder="Enter Description"
                  allowSpecialChars={true}
                />
              </div>

              <div className="col-md-12 mb-1">
                <SelectField
                  name="paymentMethodId"
                  label="Payment Method"
                  options={paymentMethodOptions}
                  placeholderText="Select Payment Method"
                  disabled={isPlaidTransaction}
                />
              </div>

              <div className="col-md-12 mb-1">
                <SelectField
                  name="payeeAccountId"
                  label="Payee Account"
                  options={payeeAccountOptions}
                  placeholderText="Select Payee Account"
                />
              </div>

              <div className="col-md-12 mb-1">
                <SelectField
                  name="tranactionCreditDebitTypeId"
                  label="Category"
                  options={transactionFlowOptions}
                  placeholderText="Select Category"
                />
              </div>

              <div className="col-md-6 mb-1">
                <InputField
                  name="amount"
                  label="Amount"
                  type="number"
                  required
                  requiredMessage="Amount is required"
                  placeholder="Enter Amount"
                  validation={{
                    validate: (value) => {
                      const amount = Number(value);
                      if (!(amount > 0)) {
                        return "Amount must be greater than 0";
                      }
                      if (amount > TRANSACTION_AMOUNT_MAX) {
                        return TRANSACTION_AMOUNT_MAX_MESSAGE;
                      }
                      return true;
                    },
                  }}
                  min="0.01"
                  allowDecimal={true}
                />
              </div>

              <div className="col-md-6 mb-1">
                <InputField
                  name="paymentDate"
                  label="Date"
                  type="date"
                  required
                  requiredMessage="Date is required"
                />
              </div>

              <div className="col-md-12 mb-1">
                <InputField
                  name="reference"
                  label="Reference"
                  placeholder="Enter Reference"
                  allowSpecialChars={true}
                />
              </div>

              <div className="col-md-12 mb-1">
                <InputField
                  name="transactionId"
                  label="Transaction ID"
                  required={
                    selectedPaymentMethodTypeId === PAYMENT_MODE.Other
                  }
                  requiredMessage="Transaction ID is required"
                  placeholder="Enter Transaction ID"
                  allowSpecialChars={true}
                />
              </div>
            </div>
          </form>
        </FormProvider>
      </Card>
    </RightSideModal>
  );
};

export default EditTransactionDrawer;
